import { ChangeEvent, FormEvent, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PageHeader from "./PageHeader";
import { FieldConfig, ResourceConfig } from "./resources";
import { useAdminLang } from "./i18n";
import FileField from "./FileField";
import JalaliDateInput from "./JalaliDateInput";

type Values = Record<string, string>;
type Status = "loading" | "idle" | "saving" | "error" | "not-found";

interface ResourceFormProps<T extends { id: number }> {
  config: ResourceConfig<T>;
}

const emptyValues = (fields: FieldConfig[]): Values =>
  Object.fromEntries(
    fields.map((f) => [f.name, f.type === "checkbox" ? "false" : f.type === "select" ? f.options?.[0]?.value ?? "" : ""])
  );

/** API value -> form value (all form values are strings). */
const toFormValue = (field: FieldConfig, value: unknown): string => {
  if (value == null) return field.type === "checkbox" ? "false" : "";
  if (field.type === "date") return String(value).slice(0, 10);
  return String(value);
};

/** form value -> API value */
const toApiValue = (field: FieldConfig, value: string): unknown => {
  if (field.type === "number") return value === "" ? 0 : Number(value);
  if (field.type === "checkbox") return value === "true";
  // an empty password means "keep the current one"; passwords are never trimmed
  if (field.type === "password") return value === "" ? null : value;
  return value.trim();
};

/** Server answer -> message shown above the form. */
const errorText = (err: any): string => {
  const status = err?.response?.status;
  if (status === 409) return "این نام کاربری یا ایمیل قبلا ثبت شده است.";
  if (status === 400) return "اطلاعات وارد شده معتبر نیست.";
  return "ارتباط با سرور انجام نشد. دوباره تلاش کنید.";
};

/** Add / edit form for a resource. With an :id route param it edits, otherwise it adds. */
export default function ResourceForm<T extends { id: number }>({ config }: ResourceFormProps<T>) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = !!id;
  const { t } = useAdminLang();

  const [values, setValues] = useState<Values>(() => emptyValues(config.fields));
  const [status, setStatus] = useState<Status>(isEdit ? "loading" : "idle");
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");

  // edit mode: load the item from the list endpoint
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    config
      .list()
      .then((items) => {
        if (cancelled) return;
        const item = items.find((i) => String(i.id) === id) as Record<string, unknown> | undefined;
        if (!item) {
          setStatus("not-found");
          return;
        }
        setValues(Object.fromEntries(config.fields.map((f) => [f.name, toFormValue(f, item[f.name])])));
        setStatus("idle");
      })
      .catch((err) => {
        console.error(err);
        if (cancelled) return;
        setError(errorText(err));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [config, id, isEdit]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const checked = e.target instanceof HTMLInputElement && e.target.type === "checkbox" ? String(e.target.checked) : null;
    setValues((prev) => ({ ...prev, [name]: checked ?? value }));
  };

  const setValue = (name: string, value: string) => setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setValidated(true);
    if (!e.currentTarget.checkValidity()) return;

    const payload = Object.fromEntries(
      config.fields.map((f) => [f.name, toApiValue(f, values[f.name])])
    ) as Omit<T, "id">;

    setStatus("saving");
    try {
      if (isEdit) await config.update!(Number(id), payload);
      else await config.create!(payload);
      navigate(`/admin/${config.path}`);
    } catch (err) {
      console.error(err);
      setError(errorText(err));
      setStatus("error");
    }
  };

  const title = `${t(isEdit ? "ویرایش" : "افزودن")} ${t(config.singular)}`;
  const backLink = `/admin/${config.path}`;

  return (
    <>
      <PageHeader title={title} crumbs={[{ title: config.title, to: backLink }]} />

      <div className="row">
        <div className="col-xl-8">
          <div className="card custom-card">
            <div className="card-header">
              <div className="card-title">
                <i className={`${config.icon} me-2 align-middle`}></i>
                {title}
              </div>
            </div>
            <div className="card-body">
              {status === "loading" ? (
                <div className="text-center p-5">
                  <div className="spinner-border text-primary" role="status"></div>
                </div>
              ) : status === "not-found" ? (
                <div className="text-center p-4">
                  <p className="text-muted">{t("این مورد پیدا نشد.")}</p>
                  <Link to={backLink} className="btn btn-sm btn-primary">{t("بازگشت به لیست")}</Link>
                </div>
              ) : (
                <form className={`row g-3 ${validated ? "was-validated" : ""}`} onSubmit={handleSubmit} noValidate>
                  {status === "error" && (
                    <div className="col-12">
                      <div className="alert alert-danger mb-0">{t(error)}</div>
                    </div>
                  )}

                  {config.fields.map((field) => {
                    const required = field.required || (field.requiredOnCreate && !isEdit);
                    const hint = isEdit && field.editHint ? field.editHint : field.hint;
                    return (
                    <div className={field.fullWidth || field.type === "textarea" ? "col-12" : "col-md-6"} key={field.name}>
                      <label htmlFor={`field-${field.name}`} className="form-label">
                        {t(field.label)}
                        {required && <span className="text-danger ms-1">*</span>}
                      </label>
                      {field.type === "select" ? (
                        <select
                          id={`field-${field.name}`}
                          name={field.name}
                          className="form-select"
                          value={values[field.name]}
                          onChange={handleChange}
                          required={field.required}
                        >
                          {field.options?.map((o) => (
                            <option key={o.value} value={o.value}>{t(o.label)}</option>
                          ))}
                        </select>
                      ) : field.type === "checkbox" ? (
                        <div className="form-check form-switch">
                          <input
                            id={`field-${field.name}`}
                            name={field.name}
                            type="checkbox"
                            className="form-check-input"
                            checked={values[field.name] === "true"}
                            onChange={handleChange}
                          />
                        </div>
                      ) : field.type === "file" ? (
                        <FileField
                          id={`field-${field.name}`}
                          kind={field.uploadKind ?? "image"}
                          value={values[field.name]}
                          onChange={(fileName) => setValue(field.name, fileName)}
                        />
                      ) : field.type === "date" ? (
                        <JalaliDateInput
                          id={`field-${field.name}`}
                          value={values[field.name]}
                          onChange={(gregorian) => setValue(field.name, gregorian)}
                          required={required}
                        />
                      ) : field.type === "textarea" ? (
                        <textarea
                          id={`field-${field.name}`}
                          name={field.name}
                          className="form-control"
                          rows={5}
                          value={values[field.name]}
                          onChange={handleChange}
                          required={field.required}
                        />
                      ) : (
                        <input
                          id={`field-${field.name}`}
                          name={field.name}
                          type={field.type}
                          className="form-control"
                          dir={field.ltr ? "ltr" : undefined}
                          min={field.min}
                          max={field.max}
                          minLength={field.minLength}
                          // minLength alone is only checked for typed values, not autofilled / pasted ones
                          pattern={field.minLength ? `.{${field.minLength},}` : undefined}
                          maxLength={field.maxLength}
                          autoComplete={field.type === "password" ? "new-password" : undefined}
                          value={values[field.name]}
                          onChange={handleChange}
                          required={required}
                        />
                      )}
                      {hint && <div className="form-text">{t(hint)}</div>}
                      <div className="invalid-feedback">
                        {field.type === "number" ? t("عددی در بازه مجاز وارد کنید.") : t("این فیلد را به درستی پر کنید.")}
                      </div>
                    </div>
                    );
                  })}

                  <div className="col-12 d-flex gap-2 mt-4">
                    <button className="btn btn-primary" type="submit" disabled={status === "saving"}>
                      {status === "saving" ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>{t("در حال ذخیره...")}
                        </>
                      ) : isEdit ? (
                        t("ذخیره تغییرات")
                      ) : (
                        title
                      )}
                    </button>
                    <Link to={backLink} className="btn btn-light">{t("انصراف")}</Link>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
